import { Observable } from "rxjs";
import { filter, take } from "rxjs/operators";
import { ofType } from "redux-observable";
import { Action } from "../../Store";
import { Omit } from "../../utils";

export const IO_REQUEST = "io/request";
export const IO_RESPONSE = "io/response";
export const IO_ERROR = "io/error";

export type REQUEST_TYPES = "GET" | "POST" | "DELETE";
export type ACTIONS = typeof IO_REQUEST | typeof IO_RESPONSE | typeof IO_ERROR;

export interface RequestAction<T = {}> extends Action<typeof IO_REQUEST> {
  url: string;
  method: REQUEST_TYPES;
  data?: T;
  meta?: any;
}

export interface ResponseAction<T = {}> extends Action<typeof IO_RESPONSE> {
  request: RequestAction;
  data: T;
  meta?: any;
}

export interface ErrorAction extends Action<typeof IO_ERROR> {
  request: RequestAction;
  error: Error;
  meta?: any;
}

// get or post action creator
export const request = <T = {}>(
  request: string,
  data?: T,
  meta?: any
): RequestAction<T> => {
  // Caller can prefix URL with the method (IE GET /ATX)
  // Parse for method or implicitly determine method based on data
  let method: REQUEST_TYPES = data ? "POST" : "GET";
  let url = request;
  let split = request.split(" ");
  let prefix = split[0];
  if (split.length > 1 && isRequestType(prefix)) {
    method = prefix;
    url = split.slice(1).join(" ");
  }
  let action: RequestAction<T> = {
    type: IO_REQUEST,
    url: url,
    method: method,
    meta: meta
  };
  if (data) action.data = data;
  return action;
};

// Response action creator
export const response = <T>(
  data: T,
  request: RequestAction,
  meta?: any
): ResponseAction<T> => {
  return {
    type: IO_RESPONSE,
    request: request,
    data: data,
    meta: meta
  };
};

export const error = (
  error: Error,
  request: RequestAction,
  meta?: any
): ErrorAction => {
  return {
    type: IO_ERROR,
    request: request,
    error: error,
    meta: meta
  };
};

// Helpful generics
// export type Omit<T, K extends keyof T> = Pick<T, Exclude<keyof T, K>>;

export type ActionPayload<TAction extends Action> = Omit<TAction, "type">;

export const actionCreator = <TAction extends Action>(type: string) => (
  payload?: ActionPayload<TAction>
) => {
  return (payload ? { ...(payload as object), type } : { type }) as TAction;
};

// Helpful pipable operatores
export const responseFrom = <T>(url: string, method: REQUEST_TYPES = "GET") => (
  obs$: Observable<ResponseAction<T>>
) =>
  obs$.pipe(
    ofType(IO_RESPONSE),
    filter(
      action => action.request.url === url && method === action.request.method
    ),
    take(1)
  );

function isRequestType(str: REQUEST_TYPES | string): str is REQUEST_TYPES {
  return str === "GET" || str === "POST" || str === "DELETE";
}

//
//
//
